<template>
  <v-dialog :value="value" max-width="640" persistent scrollable @input="$emit('input', $event)">
    <v-card v-if="order">
      <v-toolbar :color="kind === 'cancel' ? 'error' : 'orange accent-3'" dark dense flat>
        <v-toolbar-title>{{ kind === 'cancel' ? 'ขอยกเลิกการจอง' : 'ขอเลื่อนวันจัดส่ง' }}</v-toolbar-title>
      </v-toolbar>
      <v-card-text class="pt-4">
        <div class="body-2 mb-2">
          {{ order.custName || '-' }} · {{ order.tel || '-' }} · SO {{ order.soNo || 'นัดหมาย' }}<br>
          เดิม: {{ thaiDate(order.date) }} ช่วง{{ slotLabel(order.slot) }}
        </div>
        <v-alert dense text type="info" class="mb-3">
          ร้านที่ขายหรือหัวหน้ากลุ่มจะยืนยันคำขอนี้ในวันทำการถัดไป
          {{ kind === 'move' ? 'ระหว่างนี้ระบบจองที่ว่างวันใหม่ไว้ให้แล้ว' : 'การจองเดิมยังคงอยู่จนกว่าจะยืนยัน' }}
        </v-alert>

        <!-- The free seats, day by day. Every chip is slotChoices -- the rule
             proposeMove applies on save -- so what can be tapped is what the
             save accepts; a greyed chip says why. Live: the days in view are
             one bookingDay snapshot, so a seat taken meanwhile turns grey. -->
        <div v-if="kind === 'move' && !done">
          <div v-if="loadingBooking" class="caption grey--text">กำลังโหลดวันที่ว่าง...</div>
          <template v-else-if="booking">
            <div class="d-flex align-center">
              <v-btn icon small :disabled="!canShift(-1)" @click="shiftMonth(-1)"><v-icon>mdi-chevron-left</v-icon></v-btn>
              <span class="subtitle-2 mx-2">{{ monthLabel }}</span>
              <v-btn icon small :disabled="!canShift(1)" @click="shiftMonth(1)"><v-icon>mdi-chevron-right</v-icon></v-btn>
              <v-spacer />
              <span class="caption grey--text">ตัวเลข = ที่ว่าง/ทั้งหมด</span>
            </div>
            <div class="slot-grid">
              <div v-for="d in days" :key="d.date" class="d-flex align-center flex-wrap py-1 slot-row">
                <span class="body-2 day-label">{{ d.label }}</span>
                <v-chip v-for="c in d.choices" :key="c.slot" small class="ma-1"
                        :color="chipColor(d, c)" :dark="c.ok || c.current"
                        :outlined="!(date === d.date && slot === c.slot)"
                        :disabled="!c.ok && !c.current"
                        @click="c.ok && pick(d.date, c.slot)">
                  {{ slotLabel(c.slot) }} {{ chipText(c) }}
                </v-chip>
              </div>
              <div v-if="!days.length" class="caption grey--text py-2">เดือนนี้ไม่มีวันที่เลือกได้</div>
            </div>
            <div class="body-2 mt-2">
              <strong>วันใหม่ที่เลือก:</strong>
              <span v-if="date && slot">{{ thaiDate(date) }} ช่วง{{ slotLabel(slot) }}</span>
              <span v-else class="grey--text">แตะช่วงเวลาที่ว่าง (สีเขียว) เพื่อเลือก</span>
            </div>
          </template>
        </div>
        <v-select v-model="reason" :items="reasons" label="เหตุผล" class="mt-3"
                  color="orange accent-3" hide-details="auto" />
        <v-text-field v-model="note" label="หมายเหตุเพิ่มเติม (ถ้ามี)" clearable
                      color="orange accent-3" hide-details="auto" />

        <v-alert v-if="error" dense text type="error" class="mt-3 mb-0">{{ error }}</v-alert>
        <v-alert v-if="done" dense text type="success" class="mt-3 mb-0">
          <div>{{ done }}</div>
          <div v-if="planned" class="mt-1">นัดนี้วางแผนรถแล้ว — ผู้ยืนยันจะต้องวางแผนใหม่</div>
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text :disabled="saving" @click="close">ปิด</v-btn>
        <v-btn v-if="!done" :color="kind === 'cancel' ? 'error' : 'orange accent-3'" dark
               :loading="saving" :disabled="!canSave" @click="save">ส่งคำขอ</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
// Propose and confirm, from ค้นหาการจอง: a shop answering a customer about a
// booking it does not own. The transaction (proposalService) judges the seat;
// this only collects the request and reads the answer back for the customer.
import R from '@/components/bookingRules'
import moment from 'moment-timezone'
import X from '@/components/proposalRules'
import H from '@/components/bookingHistory'
import { getConfig, subscribeMonth } from '@/components/bookingService'
import { proposeMove, proposeCancel, loadBooking } from '@/components/proposalService'
import { reportError, reportWarning } from '@/components/report'

const THAI_DAY = ['อา.', 'จ.', 'อ.', 'พ.', 'พฤ.', 'ศ.', 'ส.']
const THAI_MONTH = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']

export default {
  name: 'proposalDialog',
  props: {
    value: { type: Boolean, default: false },
    order: { type: Object, default: null },
    kind: { type: String, default: 'move' }
  },
  data () {
    return { date: '', slot: '', reason: '', note: '', saving: false, error: '', done: '', planned: false,
      leadDays: null, horizonDays: null, cars: R.DEFAULT_CARS, perCar: R.PER_CAR,
      booking: null, loadingBooking: false, monthStart: '', byDate: {}, unsubMonth: null }
  },
  computed: {
    today () { return R.todayStr() },
    latest () { return R.horizonDate(R.todayStr(), this.horizonDays) },
    // The first date the save could accept: the lead time counts from the
    // order, and never before today.
    earliest () {
      const b = this.booking || {}
      const orderDate = b.orderDate || String(b.createdAt || '').slice(0, 10) || this.today
      return R.firstBookableDate(orderDate, this.leadDays, this.today)
    },
    range () { return this.monthStart ? R.bookingMonthRange(this.monthStart, this.earliest, this.latest) : null },
    monthLabel () {
      const m = moment.tz(this.monthStart || this.today, R.TZ)
      return `${THAI_MONTH[m.month()]} ${m.year() + 543}`
    },
    // Sundays are left out unless delivery opened one: nothing is delivered on
    // the others, and a row of grey chips every week is noise between the days
    // that can be picked.
    days () {
      if (!this.range || !this.booking) return []
      const out = []
      const d = moment.tz(this.range.from, R.TZ)
      const end = moment.tz(this.range.to, R.TZ)
      while (d.isSameOrBefore(end)) {
        const date = d.format('YYYY-MM-DD')
        const doc = this.byDate[date] || null
        if (!R.isClosedSunday(date, doc)) {
          out.push({
            date,
            label: `${THAI_DAY[d.day()]} ${d.format('D')} ${THAI_MONTH[d.month()]}`,
            choices: X.slotChoices(this.booking, doc, {
              date, role: this.$store.state.status, today: this.today, leadDays: this.leadDays,
              horizonDays: this.horizonDays, caps: R.capacityForDay(doc, this.cars, this.perCar)
            })
          })
        }
        d.add(1, 'day')
      }
      return out
    },
    reasons () { return R.MOVE_REASONS },
    canSave () { return !!this.reason && (this.kind === 'cancel' || (!!this.date && !!this.slot)) }
  },
  watch: {
    value (open) {
      if (!open) return this.stopMonth()
      if (!this.order) return
      Object.assign(this, { date: '', slot: '', reason: '', note: '', saving: false, error: '', done: '',
        planned: false, booking: null, byDate: {}, monthStart: '' })
      this.load()
    },
    range (r, old) {
      if (!r || (old && r.from === old.from && r.to === old.to)) return
      this.watchMonth(r)
    }
  },
  beforeDestroy () { this.stopMonth() },
  methods: {
    slotLabel (s) { return R.SLOT_LABEL_TH[s] || s || '-' },
    thaiDate (ymd) { return ymd ? H.thaiDate(ymd) : '-' },
    close () { this.$emit('input', false) },
    // The config (lead time, horizon, fleet) and, for a move, the booking the
    // chips are judged against. A config read failure falls back to the
    // defaults; a booking that cannot be read leaves only the refusal.
    async load () {
      try {
        const cfg = await getConfig()
        Object.assign(this, { leadDays: cfg.leadDays, horizonDays: cfg.horizonDays, cars: cfg.cars, perCar: cfg.perCar })
      } catch (e) {
        reportWarning('proposalDialog: getConfig failed', e)
      }
      if (this.kind !== 'move') return
      this.loadingBooking = true
      try {
        this.booking = await loadBooking(this.order.slotId)
        if (!this.booking) this.error = R.errorMessage('NOT_FOUND')
        else this.monthStart = this.earliest.slice(0, 7) + '-01'
      } catch (e) {
        reportError('proposalDialog: loadBooking failed', e)
        this.error = 'โหลดวันที่ว่างไม่ได้ โปรดลองใหม่'
      } finally {
        this.loadingBooking = false
      }
    },
    watchMonth (r) {
      this.stopMonth()
      this.unsubMonth = subscribeMonth(r.from, r.to, byDate => { this.byDate = byDate },
        e => reportError('proposalDialog: bookingDay subscription failed', e))
    },
    stopMonth () {
      if (this.unsubMonth) this.unsubMonth()
      this.unsubMonth = null
    },
    canShift (n) {
      if (!this.monthStart) return false
      const next = moment.tz(this.monthStart, R.TZ).add(n, 'month').format('YYYY-MM-DD')
      return !!R.bookingMonthRange(next, this.earliest, this.latest)
    },
    shiftMonth (n) {
      if (!this.canShift(n)) return
      this.monthStart = moment.tz(this.monthStart, R.TZ).add(n, 'month').format('YYYY-MM-DD')
    },
    pick (date, slot) {
      this.date = date
      this.slot = slot
    },
    chipText (c) {
      if (c.current) return '· นัดเดิม'
      if (!c.ok) return `· ${c.label}`
      return `${c.left}/${c.cap}`
    },
    chipColor (d, c) {
      if (c.current) return 'blue-grey'
      if (!c.ok) return 'grey lighten-1'
      if (this.date === d.date && this.slot === c.slot) return 'orange accent-3'
      return c.left <= 2 ? 'amber darken-2' : 'green'
    },
    async save () {
      this.saving = true
      this.error = ''
      const who = {
        user: this.$store.state.user || '',
        role: this.$store.state.status,
        identities: this.$store.getters.identities
      }
      try {
        const r = this.kind === 'cancel'
          ? await proposeCancel(this.order.slotId, Object.assign({ reason: this.reason, note: this.note || '' }, who))
          : await proposeMove(this.order.slotId, Object.assign({ date: this.date, slot: this.slot,
            reason: this.reason, note: this.note || '' }, who))
        this.planned = !!r.planned
        // Read to the customer, word for word.
        this.done = this.kind === 'cancel'
          ? 'รับเรื่องขอยกเลิกแล้ว — ร้านจะยืนยันภายในวันทำการถัดไป'
          : `จองวันใหม่ไว้ให้แล้ว ${this.thaiDate(this.date)} ช่วง${this.slotLabel(this.slot)} — ร้านจะยืนยันภายในวันทำการถัดไป`
        this.$emit('saved')
      } catch (e) {
        if (!e.reason) reportError('propose failed', e)
        this.error = e.reason ? R.errorMessage(e.reason, { leadDays: this.leadDays, horizonDays: this.horizonDays }) : 'ส่งคำขอไม่สำเร็จ โปรดลองใหม่'
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.slot-grid { max-height: 320px; overflow-y: auto; border-top: 1px solid rgba(0, 0, 0, 0.12); }
.slot-row { border-bottom: 1px solid rgba(0, 0, 0, 0.06); }
.day-label { width: 92px; flex: 0 0 92px; }
</style>
